import { createFeature, createReducer, on } from '@ngrx/store';

import {
  EMPTY_SUPPLIERS_META_DATA,
  ManageSupplierState,
} from './manage-supplier.model';
import {
  ManageSupplierActions,
  ManageSupplierApiActions,
} from './manage-supplier.actions';

const initialState: ManageSupplierState = {
  survey: null,
  allocations: [],
  feasibilitySuppliers: [],
  blends: [],
  groupTemplates: [],
  defaultAllocations: [],
  rateCards: null,
  suppliersMetaData: EMPTY_SUPPLIERS_META_DATA,
  requested: false,
  loading: false,
  loadError: null,
  allocationsUpdateStatus: 'idle',
  blendSaving: false,
  savedBlendId: null,
};

export const manageSupplierReducer = createReducer(
  initialState,

  on(
    ManageSupplierActions.load,
    (state): ManageSupplierState => ({
      ...state,
      requested: true,
      loading: true,
      loadError: null,
      allocationsUpdateStatus: 'idle',
    }),
  ),

  on(
    ManageSupplierApiActions.loadSuccess,
    (
      state,
      {
        survey,
        allocations,
        feasibilitySuppliers,
        blends,
        groupTemplates,
        defaultAllocations,
        rateCards,
        suppliersMetaData,
      },
    ): ManageSupplierState => ({
      ...state,
      survey,
      allocations,
      feasibilitySuppliers,
      blends,
      groupTemplates,
      defaultAllocations,
      rateCards,
      suppliersMetaData,
      loading: false,
      loadError: null,
    }),
  ),

  on(
    ManageSupplierApiActions.loadFailure,
    (state, { error }): ManageSupplierState => ({
      ...state,
      loading: false,
      loadError: error,
    }),
  ),

  on(
    ManageSupplierActions.updateManagedAllocations,
    (state): ManageSupplierState => ({
      ...state,
      allocationsUpdateStatus: 'saving',
    }),
  ),

  // The PUT returns the survey's updated allocations, so its response replaces
  // the list outright. The blend id is mirrored so a re-open preselects it.
  on(
    ManageSupplierApiActions.updateAllocationsSuccess,
    (state, { allocations, blendId }): ManageSupplierState => ({
      ...state,
      allocations,
      allocationsUpdateStatus: 'saved',
      suppliersMetaData: { ...state.suppliersMetaData, blendId },
    }),
  ),

  // The previous list survives a failed update so the drawer can stay open on
  // the user's unsaved edits.
  on(
    ManageSupplierApiActions.updateAllocationsFailure,
    (state): ManageSupplierState => ({
      ...state,
      allocationsUpdateStatus: 'failed',
    }),
  ),

  on(
    ManageSupplierActions.saveBlend,
    (state): ManageSupplierState => ({
      ...state,
      blendSaving: true,
      savedBlendId: null,
    }),
  ),

  on(
    ManageSupplierApiActions.saveBlendSuccess,
    (state, { blend }): ManageSupplierState => ({
      ...state,
      blendSaving: false,
      savedBlendId: blend.id,
    }),
  ),

  on(
    ManageSupplierApiActions.saveBlendFailure,
    (state): ManageSupplierState => ({
      ...state,
      blendSaving: false,
      savedBlendId: null,
    }),
  ),

  on(
    ManageSupplierApiActions.reloadBlendsSuccess,
    (state, { blends }): ManageSupplierState => ({
      ...state,
      blends,
    }),
  ),

  on(ManageSupplierActions.reset, (): ManageSupplierState => initialState),
);

export const manageSupplierFeature = createFeature({
  name: 'manageSupplier',
  reducer: manageSupplierReducer,
});
